import React, { useMemo } from 'react';
import { DndContext, closestCenter, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslate } from 'piral-translate';
import { useTilesStore } from '../../siplet-api/tiles.store';
import { DragHandleIcon } from '../Icons/Icons';

type TileGroup = 'favorite' | 'standard';

interface SortableTileContainerProps {
  children: React.ReactNode[];
  group: TileGroup;
  className?: string;
}

interface SortableTileProps {
  id: string;
  children: React.ReactNode;
  dragTitle: string;
}

const SortableTile: React.FC<SortableTileProps> = ({ id, children, dragTitle }) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id,
    transition: {
      duration: 250,
      easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
    },
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: transition || 'transform 300ms cubic-bezier(0.25, 1, 0.5, 1)',
    position: 'relative',
  };

  return (
    <div ref={setNodeRef} style={style} className={isDragging ? 'dragging' : ''}>
      <div
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        className="neo-tile-drag-handle"
        title={dragTitle}>
        <DragHandleIcon />
      </div>
      {children}
    </div>
  );
};

export const SortableTileContainer: React.FC<SortableTileContainerProps> = ({ children, group, className }) => {
  const { updateOrder, getOrderIndex } = useTilesStore();
  const translate = useTranslate();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  // Extract tile keys and sort children
  const { sortedChildren, tileIds } = useMemo(() => {
    const tiles = React.Children.toArray(children).map(child => {
      const key = (child as React.ReactElement).key?.toString().split('//')[1] || (child as React.ReactElement).key?.toString() || '';
      return { child, key };
    });

    // Sort by stored order (unknown tiles go to end)
    tiles.sort((a, b) => {
      const indexA = getOrderIndex(group, a.key);
      const indexB = getOrderIndex(group, b.key);
      return indexA - indexB;
    });

    return {
      sortedChildren: tiles.map(t => t.child),
      tileIds: tiles.map(t => t.key),
    };
  }, [children, group, getOrderIndex]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = tileIds.indexOf(active.id as string);
      const newIndex = tileIds.indexOf(over.id as string);
      const newOrder = arrayMove(tileIds, oldIndex, newIndex);
      updateOrder(group, newOrder);
    }
  };

  // If there's only one tile or no tiles, just render without DnD
  if (sortedChildren.length <= 1) {
    return <div className={className}>{sortedChildren}</div>;
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={tileIds} strategy={rectSortingStrategy}>
        <div className={className}>
          {sortedChildren.map((child, index) => (
            <SortableTile key={tileIds[index]} id={tileIds[index]} dragTitle={translate('drag-to-reorder')}>
              {child}
            </SortableTile>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};
